/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Today — the first screen after sign-in.
 *
 * ── WHAT THIS SCREEN IS FOR ─────────────────────────────────────────────────
 * Answering "what should I do right now?" in under three seconds. Everything
 * below the NextCard is context for that answer, not competition with it.
 *
 * Previously this was a dashboard of six equal panels, which is a different
 * thing: a dashboard reports, and reporting is only useful to someone who
 * already knows what they're looking for. The panels are still here — they're
 * good — but they now sit under a card that has made the decision for you.
 *
 * ── THE DATE ────────────────────────────────────────────────────────────────
 * Every date rendered here arrives in the response, computed by the server in
 * the household's timezone. Nothing in this file calls `new Date()` to decide
 * what day it is; the only `new Date` is for formatting a timestamp the server
 * already produced. Same contract as PlanView.
 *
 * ── ONE REQUEST ─────────────────────────────────────────────────────────────
 * /api/home returns the whole screen. That is deliberate: on a phone on
 * cellular, six parallel fetches means six independent chances to show a
 * spinner, and a half-populated dashboard reads as broken.
 *
 * ── AND WHY IT IS NOW react-query ───────────────────────────────────────────
 * This file and FamilyView were the last two using useState+useEffect while
 * every other screen used react-query. The user-visible cost was real: adding a
 * recipe invalidated `["home"]` from three different mutations, and this screen
 * ignored all of them, so Today could sit there recommending "add your first
 * recipe" after you had just added one.
 */

import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HomeApi } from "../../api/endpoints.ts";
import type { HomeResponse } from "../../api/types.ts";
import { ErrorBox, Loading, Spinner } from "../../components/ui.tsx";
import { WeekRibbon } from "../../components/WeekRibbon.tsx";
import { Icon } from "../../components/Icon.tsx";
import { useToast } from "../../components/Toast.tsx";
import { usePrefs } from "../../i18n/index.tsx";
import { NextCard } from "./NextCard.tsx";
import { GettingStarted } from "./GettingStarted.tsx";
import { GroupActivityCard } from "./GroupActivityCard.tsx";
import { formatDay, formatWeekday, relativeDayLabel } from "../../lib/dates.ts";

export function HomeView() {
  const { t, lang } = usePrefs();
  const qc = useQueryClient();
  const toast = useToast();

  const home = useQuery({
    queryKey: ["home"],
    queryFn: () => HomeApi.get(),
  });

  const refreshBrief = useMutation({
    mutationFn: () => HomeApi.refreshBrief(),
    onSuccess: ({ brief }) => {
      qc.setQueryData<HomeResponse>(["home"], (d) => (d ? { ...d, brief } : d));
    },
    // This used to be `catch { /* nothing is lost */ }`. Nothing was lost, but
    // nothing was said either: you pressed a button, an AI call failed, and the
    // screen sat there unchanged. Indistinguishable from a dead button.
    onError: (e: Error) => toast(e.message || "Couldn't fetch new ideas.", "danger"),
  });

  if (home.error && !home.data) {
    return (
      <ErrorBox
        message={(home.error as Error).message || t("common.loadFailed")}
        onRetry={() => void home.refetch()}
      />
    );
  }
  if (!home.data) return <Loading label={t("home.loading")} />;

  const data = home.data;
  const { brief, meals, week, suggestions, shoppingList, eaters } = data;
  const allergens = [...new Set(eaters.flatMap((e) => e.allergens))];
  const diets = [...new Set(eaters.flatMap((e) => e.dietTags))];

  return (
    <div className="animate-fade-in space-y-5">
      {/* ── The date, stated plainly. Server-computed, household timezone. ── */}
      <header>
        <div className="label-mono text-accent">{formatWeekday(data.today, lang)}</div>
        <h1 className="type-display mt-1">{formatDay(data.today, lang)}</h1>
        <p className="mt-2 max-w-prose text-ink-2">{brief.headline}</p>
      </header>

      {/* ── The one next thing ───────────────────────────────────────────── */}
      <NextCard data={data} />

      {/* The full checklist stays available beneath the recommendation, so
          someone who wants the whole picture can see all four steps and how far
          through they are. It hides itself once setup is done. */}
      <GettingStarted setup={data.setup} />

      {data.clockWarning && (
        <div className="card border-warn/40 bg-warn/5 p-3 text-sm text-ink-2">
          {data.clockWarning}
        </div>
      )}

      {/*
        ── TWO COLUMNS FROM lg UP ────────────────────────────────────────────
        The main column is the weekly loop — the week, tonight's meals, ideas.
        The rail is the social side: what other households have been cooking.

        The split is the point. Cooking groups needed to be visible, but putting
        them IN the main column would have them competing with "what are we
        eating tonight", and losing that argument would make this a social app
        that occasionally plans meals. A rail is present without being in the
        way.

        Below lg the rail stacks BELOW the main column rather than above it —
        `order-2` — so a phone still leads with the plan.
      */}
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="order-1 space-y-5 lg:col-span-2">

      {/* ── The week, as the shared ribbon ───────────────────────────────── */}
      <section className="card p-4">
        <div className="mb-2.5 flex items-baseline justify-between">
          <h2 className="label-mono">{t("home.weekAhead")}</h2>
          {data.emptyDaysThisWeek > 0 && (
            <span className="text-xs text-ink-3">
              {t("home.emptyDays").replace("{n}", String(data.emptyDaysThisWeek))}
            </span>
          )}
        </div>
        <WeekRibbon
          days={week.map((d) => ({ date: d.date, count: d.count, isToday: d.isToday }))}
          activeDate={data.today}
          onPick={undefined}
        />
      </section>

      {/* ── Today ────────────────────────────────────────────────────────── */}
      <section className="card p-4">
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="label-mono">{t("home.today")}</h2>
          <Link to="/plan" className="text-xs text-accent hover:underline">
            {t("home.openPlan")}
          </Link>
        </div>
        {meals.length === 0 ? (
          <p className="text-sm text-ink-3">
            {t("home.nothingToday")}{" "}
            <Link to="/plan" className="text-accent hover:underline">{t("home.planSomething")}</Link>
          </p>
        ) : (
          <ul className="divide-y divide-rule-soft">
            {meals.map((m) => (
              <li key={m.id} className="flex items-center gap-3 py-2">
                <span className="label-mono w-20 shrink-0 text-ink-3">{m.slotKey}</span>
                <span className={m.status === "skipped" ? "line-through text-ink-3" : "text-ink"}>
                  {m.recipeId
                    ? <Link to={`/recipes/${m.recipeId}`} className="hover:underline">{m.title}</Link>
                    : m.title}
                </span>
                <span className="ml-auto flex items-center gap-2 text-xs text-ink-3">
                  {m.totalMinutes ? <span>{m.totalMinutes} {t("common.minutes")}</span> : null}
                  <span>{m.servings} {t("common.servings")}</span>
                  {m.status === "cooked" && (
                    <Icon name="check" className="h-4 w-4 text-accent" label="Cooked" />
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Ideas ──────────────────────────────────────────────────────────
          Hidden entirely while the library is empty: an ideas card with
          nothing in it is worse than no card, and the NextCard is already
          saying what to do. */}
      {(brief.ideas.length > 0 || suggestions.length > 0) && (
      <section className="card p-4">
        <div className="mb-1 flex items-baseline justify-between">
          <h2 className="label-mono">{t("home.ideas")}</h2>
          <button className="btn-ghost !px-2 !py-1 text-xs"
                  onClick={() => refreshBrief.mutate()}
                  disabled={refreshBrief.isPending}>
            {refreshBrief.isPending ? <Spinner className="h-3 w-3" /> : t("home.newIdeas")}
          </button>
        </div>
        <p className="mb-3 text-xs text-ink-3">
          {/* Saying which is not pedantry: it is the difference between "the AI
              is switched off" and "the AI is broken", which you cannot tell
              from the text alone. */}
          {brief.fallback ? t("home.ideasLocal") : t("home.ideasAi")}
          {allergens.length > 0 && ` · ${t("home.avoiding")} ${allergens.join(", ")}`}
          {diets.length > 0 && ` · ${diets.join(", ")}`}
        </p>
        {brief.ideas.length === 0 ? (
          <p className="text-sm text-ink-3">{t("home.noIdeas")}</p>
        ) : (
          <ul className="space-y-2">
            {brief.ideas.map((idea, i) => (
              <li key={`${idea.title}-${i}`} className="rounded-lg border border-rule-soft p-3">
                <div className="font-medium text-ink">{idea.title}</div>
                {idea.why && <div className="mt-0.5 text-sm text-ink-2">{idea.why}</div>}
                <Link to={`/recipes/generate?title=${encodeURIComponent(idea.title)}`}
                      className="mt-2 inline-block text-xs text-accent hover:underline">
                  {t("home.makeRecipe")}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
      )}

        </div>

        {/* ── The rail ─────────────────────────────────────────────────────
            Stacked below the plan on a phone (order-2), beside it from lg. */}
        <aside className="order-2 space-y-5">
          <GroupActivityCard activity={data.circles} />

          {/* ── Not cooked in a while ────────────────────────────────────── */}
          {suggestions.length > 0 && (
            <section className="card p-4">
              <h2 className="label-mono mb-3">{t("home.fromLibrary")}</h2>
              <ul className="space-y-1">
                {suggestions.map((s) => (
                  <li key={s.id} className="flex items-baseline justify-between gap-3 py-1">
                    <Link to={`/recipes/${s.id}`} className="text-ink hover:underline">{s.title}</Link>
                    <span className="shrink-0 text-xs text-ink-3">{s.lastCookedLabel}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ── Shopping ─────────────────────────────────────────────────── */}
          {shoppingList && (
            <section className="card p-4">
              <h2 className="label-mono mb-3">{t("home.shopping")}</h2>
              <Link to={`/shopping/${shoppingList.id}`} className="text-ink hover:underline">
                {shoppingList.title}
              </Link>
              <p className="mt-1 text-sm text-ink-2">
                {t("home.itemsLeft")
                  .replace("{open}", String(shoppingList.openItems))
                  .replace("{total}", String(shoppingList.totalItems))}
              </p>
              <Link to={`/shopping/${shoppingList.id}/mode`} className="btn-ghost mt-3 text-xs">
                {t("home.startShopping")}
              </Link>
            </section>
          )}
        </aside>
      </div>

      {brief.generatedAt && !brief.fallback && (
        <p className="text-center text-[11px] text-ink-3">
          {t("home.generatedAt").replace(
            "{time}",
            new Date(brief.generatedAt).toLocaleTimeString(lang === "fr" ? "fr-FR" : "en-US",
              { hour: "2-digit", minute: "2-digit" }),
          )}
        </p>
      )}
      <p className="sr-only">{relativeDayLabel(data.today, data.today, lang)}</p>
    </div>
  );
}
